<template>
  <b-overlay
    :show="formShow"
    rounded="true"
    no-fade
    style="height:100vh!important"
  >
    <div>
      <b-card class="mt-2">
        <b-row class="mb-2">
          <table class="table">
            <thead>
              <tr>
                <th scope="col" style="font-size: 1.2em;">
                  REVIEW SUMMARY EXPLANATION - {{ reviewTitle }}
                </th>
              </tr>
            </thead>
          </table>
        </b-row>

        <div class="m-5" v-if="maxScore > 0">
          <vue-slider
            :min="0"
            :max="maxScore"
            :tooltip="'always'"
            :dot-options="dotOptions"
            v-model="ranges"
            :interval="1"
          />
        </div>

        <b-alert variant="info" show>
          <div class="alert-body">
            <span>
              Please type
              <strong class="bg-danger p-25 text-white">#totalScore#</strong>
              inside the sentence in order to add the calculated total
              score.</span
            >
          </div>
        </b-alert>

        <b-row
          class="mb-3 ml-2 mr-2"
          v-if="rangeDatas && rangeDatas.length >= 4 && maxScore > 0"
        >
          <b-col cols="12" class="mt-2" v-if="rangeDatas[0] && rangeDatas[1]">
            <label for="textarea-1"
              >{{ rangeDatas[0].range || 0 }} -
              {{ rangeDatas[1].range || 0 }} summary explanation</label
            >
            <b-form-textarea
              id="textarea-1"
              v-model="rangeDatas[0].title"
              rows="2"
            />
          </b-col>

          <b-col cols="12" class="mt-2" v-if="rangeDatas[1] && rangeDatas[2]">
            <label for="textarea-2"
              >{{ (rangeDatas[1].range || 0) + 1 }} -
              {{ rangeDatas[2].range || 0 }} summary explanation</label
            >
            <b-form-textarea
              id="textarea-2"
              v-model="rangeDatas[1].title"
              rows="2"
            />
          </b-col>

          <b-col cols="12" class="mt-2" v-if="rangeDatas[2] && rangeDatas[3]">
            <label for="textarea-3"
              >{{ (rangeDatas[2].range || 0) + 1 }} -
              {{ rangeDatas[3].range || 0 }} summary explanation</label
            >
            <b-form-textarea
              id="textarea-3"
              v-model="rangeDatas[2].title"
              rows="2"
            />
          </b-col>

          <b-col cols="12" class="mt-2" v-if="rangeDatas[3]">
            <label for="textarea-4"
              >{{ (rangeDatas[3].range || 0) + 1 }} - {{ maxScore }} summary
              explanation</label
            >
            <b-form-textarea
              id="textarea-4"
              v-model="rangeDatas[3].title"
              rows="2"
            />
          </b-col>
        </b-row>

        <b-button
          type="submit"
          @click="updateSummaryExplanation"
          variant="warning"
          class="mr-3 float-right"
          :disabled="maxScore === 0"
        >
          Save
        </b-button>

        <b-button variant="secondary" class="mr-3 float-right" @click="goBack">
          Back to Review
        </b-button>
      </b-card>
    </div>
  </b-overlay>
</template>

<script>
import {
  BFormGroup,
  BTable,
  BRow,
  BCol,
  BFormSelect,
  BPagination,
  BIcon,
  BSkeletonIcon,
  BFormRating,
  BAlert,
  BButton,
  BCard,
  BOverlay,
  BFormTextarea,
} from "bootstrap-vue";
import settingsStoreModule from "../settingsStoreModule";
import store from "@/store";
import { ref, onUnmounted } from "@vue/composition-api";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import _ from "lodash";
import VueSlider from "vue-slider-component";
import axiosIns from "@/libs/axios";

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BFormGroup,
    BFormSelect,
    BPagination,
    BIcon,
    BFormRating,
    BSkeletonIcon,
    BAlert,
    BButton,
    BCard,
    BOverlay,
    BFormTextarea,
    ValidationProvider,
    ValidationObserver,
    VueSlider,
  },
  data() {
    return {
      surveyId: 0,
      reviewTitle: "",
      maxScore: 0,
      rangeDatas: [],
      ranges: [0, 0, 0, 0, 0],
      formShow: false,
      dotOptions: [
        {
          disabled: true, // First point (0) - always disabled
        },
        {
          disabled: false, // Second point - movable
        },
        {
          disabled: false, // Third point - movable
        },
        {
          disabled: false, // Fourth point - movable
        },
        {
          disabled: true, // Last point (maxScore) - always disabled
        },
      ],
    };
  },
  setup() {
    const SETTINGS_APP_STORE_MODULE_NAME = "settings";
    // Register module
    if (!store.hasModule(SETTINGS_APP_STORE_MODULE_NAME))
      store.registerModule(SETTINGS_APP_STORE_MODULE_NAME, settingsStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SETTINGS_APP_STORE_MODULE_NAME))
        store.unregisterModule(SETTINGS_APP_STORE_MODULE_NAME);
    });
  },
  computed: {},
  watch: {
    ranges(val) {
      if (
        this.rangeDatas &&
        Array.isArray(this.rangeDatas) &&
        this.rangeDatas.length >= 4 &&
        val &&
        Array.isArray(val) &&
        val.length >= 4
      ) {
        if (this.rangeDatas[0]) this.rangeDatas[0].range = val[0];
        if (this.rangeDatas[1]) this.rangeDatas[1].range = val[1];
        if (this.rangeDatas[2]) this.rangeDatas[2].range = val[2];
        if (this.rangeDatas[3]) this.rangeDatas[3].range = val[3];
      }
    },
  },
  methods: {
    getReviewMaxScore() {
      this.formShow = true;

      store
        .dispatch("settings/fetchQuestionnaireDetail", this.surveyId)
        .then((res) => {
          this.reviewTitle = res.data.title;

          // Calculate max score from all questions
          let totalMaxScore = 0;
          if (res.data.groups && res.data.groups.length > 0) {
            res.data.groups.forEach((group) => {
              if (group.questions && group.questions.length > 0) {
                group.questions.forEach((question) => {
                  if (question.maxScore) {
                    totalMaxScore += parseInt(question.maxScore);
                  }
                });
              }
            });
          }

          this.maxScore = totalMaxScore;

          if (this.maxScore > 0) {
            this.initializeRanges();
            this.getReviewSummaryExplanation();
          } else {
            // If maxScore is 0, initialize with default values
            this.maxScore = 100; // Set default maxScore
            this.initializeFallbackRanges();
            this.formShow = false;
          }
        })
        .catch((error) => {
          this.formShow = false;
          console.log(error);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              text: "Failed to load quality review data",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    },

    initializeRanges() {
      // Try to get ranges from default summary (surveyId = 0) or fallback to simple ranges
      axiosIns
        .get(`summaryList/0`)
        .then((res) => {
          if (
            res.data &&
            res.data.title &&
            Array.isArray(res.data.title) &&
            res.data.title.length >= 4
          ) {
            // Use percentage ranges from default summary and convert to maxScore proportions
            const defaultRanges = res.data.title;

            this.ranges = [
              0,
              Math.round(
                ((defaultRanges[1] && defaultRanges[1].range
                  ? defaultRanges[1].range
                  : 25) /
                  100) *
                  this.maxScore
              ),
              Math.round(
                ((defaultRanges[2] && defaultRanges[2].range
                  ? defaultRanges[2].range
                  : 50) /
                  100) *
                  this.maxScore
              ),
              Math.round(
                ((defaultRanges[3] && defaultRanges[3].range
                  ? defaultRanges[3].range
                  : 75) /
                  100) *
                  this.maxScore
              ),
              this.maxScore,
            ];

            // Initialize rangeDatas with default explanations, using the default summary titles
            this.rangeDatas = [
              {
                range: 0,
                title:
                  (defaultRanges[0] &&
                  defaultRanges[0].title &&
                  defaultRanges[0].title.replace
                    ? defaultRanges[0].title.replace(
                        "#domainScore#",
                        "#totalScore#"
                      )
                    : null) ||
                  `Low performance range (#totalScore# out of ${this.maxScore})`,
              },
              {
                range: this.ranges[1],
                title:
                  (defaultRanges[1] &&
                  defaultRanges[1].title &&
                  defaultRanges[1].title.replace
                    ? defaultRanges[1].title.replace(
                        "#domainScore#",
                        "#totalScore#"
                      )
                    : null) ||
                  `Below average performance range (#totalScore# out of ${this.maxScore})`,
              },
              {
                range: this.ranges[2],
                title:
                  (defaultRanges[2] &&
                  defaultRanges[2].title &&
                  defaultRanges[2].title.replace
                    ? defaultRanges[2].title.replace(
                        "#domainScore#",
                        "#totalScore#"
                      )
                    : null) ||
                  `Average performance range (#totalScore# out of ${this.maxScore})`,
              },
              {
                range: this.ranges[3],
                title:
                  (defaultRanges[3] &&
                  defaultRanges[3].title &&
                  defaultRanges[3].title.replace
                    ? defaultRanges[3].title.replace(
                        "#domainScore#",
                        "#totalScore#"
                      )
                    : null) ||
                  `Good performance range (#totalScore# out of ${this.maxScore})`,
              },
            ];
          } else {
            // Fallback to simple division if default summary is not available
            this.initializeFallbackRanges();
          }
        })
        .catch((error) => {
          this.initializeFallbackRanges();
        });
    },

    initializeFallbackRanges() {
      // Calculate default ranges (divide maxScore into 4 parts)
      const quarter = Math.floor(this.maxScore / 4);

      this.ranges = [0, quarter, quarter * 2, quarter * 3, this.maxScore];

      // Initialize rangeDatas with default explanations
      this.rangeDatas = [
        {
          range: 0,
          title: `Low performance range (#totalScore# out of ${this.maxScore})`,
        },
        {
          range: quarter,
          title: `Below average performance range (#totalScore# out of ${this.maxScore})`,
        },
        {
          range: quarter * 2,
          title: `Average performance range (#totalScore# out of ${this.maxScore})`,
        },
        {
          range: quarter * 3,
          title: `Good performance range (#totalScore# out of ${this.maxScore})`,
        },
      ];
    },

    getReviewSummaryExplanation() {
      axiosIns
        .get(`review-summary-explanation/${this.surveyId}`)
        .then((res) => {
          if (res.data && res.data.summaryExplanation) {
            const summaryData = JSON.parse(res.data.summaryExplanation);

            if (summaryData && summaryData.ranges && summaryData.explanations) {
              // Validate and fix ranges array if corrupted
              let validRanges = summaryData.ranges;

              if (!Array.isArray(validRanges) || validRanges.length !== 5) {
                console.warn("Invalid ranges detected, fixing...", validRanges);
                // Create default ranges if invalid
                const quarter = Math.floor(this.maxScore / 4);
                validRanges = [
                  0,
                  quarter,
                  quarter * 2,
                  quarter * 3,
                  this.maxScore,
                ];
              }

              // Ensure first element is 0 and last is maxScore
              validRanges[0] = 0;
              validRanges[4] = this.maxScore;

              this.ranges = validRanges;
              this.rangeDatas = summaryData.explanations || [];

              // Ensure we have exactly 4 range data items
              while (this.rangeDatas.length < 4) {
                const index = this.rangeDatas.length;
                this.rangeDatas.push({
                  range: this.ranges[index] || 0,
                  title: "",
                });
              }

              // Update rangeDatas ranges to match corrected ranges
              for (let i = 0; i < Math.min(4, this.rangeDatas.length); i++) {
                if (this.rangeDatas[i]) {
                  this.rangeDatas[i].range = this.ranges[i];
                }
              }
            } else {
              // If no valid summary data, initialize with default values
              this.initializeFallbackRanges();
            }
          } else {
            // If no summary explanation, initialize with default values
            this.initializeFallbackRanges();
          }
          this.formShow = false;
        })
        .catch((error) => {
          this.formShow = false;
          // If error, initialize with default values
          this.initializeFallbackRanges();
        });
    },

    updateSummaryExplanation() {
      this.formShow = true;

      // Ensure ranges array is always properly formatted with 5 elements
      let validRanges = [...this.ranges];

      // Validate and fix ranges array
      if (!Array.isArray(validRanges) || validRanges.length !== 5) {
        // Create default ranges if invalid
        const quarter = Math.floor(this.maxScore / 4);
        validRanges = [0, quarter, quarter * 2, quarter * 3, this.maxScore];
      }

      // Ensure first element is 0 and last is maxScore
      validRanges[0] = 0;
      validRanges[4] = this.maxScore;

      // Ensure ranges are in ascending order
      for (let i = 1; i < 4; i++) {
        if (
          validRanges[i] <= validRanges[i - 1] ||
          validRanges[i] >= validRanges[i + 1]
        ) {
          validRanges[i] = Math.round(
            validRanges[i - 1] + (this.maxScore - validRanges[i - 1]) / (5 - i)
          );
        }
      }

      const summaryData = {
        ranges: validRanges,
        explanations: this.rangeDatas,
        maxScore: this.maxScore,
      };

      axiosIns
        .post(`review-summary-explanation/${this.surveyId}`, {
          summaryExplanation: JSON.stringify(summaryData),
        })
        .then((res) => {
          this.formShow = false;
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Successful",
              text: "✔️ Summary Explanation Updated Successfully",
              icon: "ThumbsUpIcon",
              variant: "success",
            },
          });
        })
        .catch((error) => {
          this.formShow = false;
          console.log(error);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              text: "Failed to update summary explanation",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    },

    goBack() {
      this.$router.push({
        name: "settings-edit-review",
        params: { id: this.surveyId },
      });
    },
  },
  created() {
    this.surveyId = this.$route.params.id;
    this.getReviewMaxScore();
  },
};
</script>

<style lang="scss">
@import "~@core/scss/vue/libs/vue-slider.scss";
</style>
